/**
 * Share-listing dialog.
 *
 * Surfaced from the listing detail (owner view) and the public listing
 * page (any visitor). Three actions, each independent and best-effort:
 *
 *   - Native share (mobile): only rendered when navigator.share is
 *     available; clicking it opens the OS share sheet so the realtor
 *     can pick WhatsApp Status, Instagram Story, SMS, etc.
 *   - WhatsApp: opens a wa.me deep link with a pre-formatted message,
 *     works on every device (web + mobile).
 *   - Copy link: clipboard fallback, also useful for SMS / email.
 *
 * Each action is its own button rather than chained behaviour so the
 * realtor's tap maps cleanly to the destination they expect.
 */

import { Check, Copy, MessageCircle, Share2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import {
	buildListingShareContent,
	copyToClipboard,
	type ShareableListing,
	shareViaBrowser,
} from "@/features/realtor/utils/share-listing";
import { StudioButton } from "@/features/studio/theme";
import { cn } from "@/lib/utils";

interface ShareListingDialogProps {
	listing: ShareableListing;
	triggerLabel?: string;
	triggerClassName?: string;
	triggerVariant?: "primary" | "secondary" | "outline" | "ghost" | "link";
	triggerSize?: "default" | "sm" | "lg" | "icon";
	hideIcon?: boolean;
}

export function ShareListingDialog({
	listing,
	triggerLabel,
	triggerClassName,
	triggerVariant = "secondary",
	triggerSize = "sm",
	hideIcon = false,
}: ShareListingDialogProps) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [copied, setCopied] = useState(false);
	// Lazy initializer is safe in this Vite SPA (no SSR), and avoids the
	// one-frame flicker where a useEffect-driven check would mount the
	// dialog without the native button on slow devices.
	const [hasNativeShare] = useState(
		() => typeof navigator !== "undefined" && Boolean(navigator.share),
	);
	const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Clear the "copied" indicator timer on unmount so we don't setState
	// after the dialog dismounts.
	useEffect(() => {
		return () => {
			if (copiedTimerRef.current) {
				clearTimeout(copiedTimerRef.current);
				copiedTimerRef.current = null;
			}
		};
	}, []);

	const content = buildListingShareContent(listing);

	if (!content) {
		// Listing has no public slug — share button is hidden by the
		// caller in that case, but we double-check to keep the component
		// safe to mount unconditionally.
		return null;
	}

	const handleNativeShare = async () => {
		const ok = await shareViaBrowser(content);
		if (!ok) {
			toast.error(t("realtor.listings.share.nativeFailedFallback"));
			return;
		}
		setOpen(false);
	};

	const handleWhatsApp = () => {
		window.open(content.whatsappUrl, "_blank", "noopener,noreferrer");
		setOpen(false);
	};

	const handleCopy = async () => {
		const ok = await copyToClipboard(content.url);
		if (!ok) {
			toast.error(t("realtor.listings.share.copyFailedFallback"));
			return;
		}
		setCopied(true);
		toast.success(t("realtor.listings.share.copiedToast"));
		if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
		copiedTimerRef.current = setTimeout(() => {
			setCopied(false);
			copiedTimerRef.current = null;
		}, 1500);
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<StudioButton
					variant={triggerVariant as unknown as "secondary"}
					size={triggerSize as unknown as "sm"}
					className={cn(
						"gap-1.5 border-dashboard-border text-dashboard-text bg-dashboard-bg hover:bg-dashboard-surface",
						triggerClassName,
					)}
				>
					{!hideIcon && <Share2 className="h-4 w-4" />}
					{triggerLabel ??
						(!hideIcon ? t("realtor.listings.share.trigger") : null)}
				</StudioButton>
			</DialogTrigger>
			<DialogContent className="bg-dashboard-bg border-dashboard-border rounded-xl">
				<DialogHeader>
					<DialogTitle
						style={{
							fontFamily: "var(--font-display)",
							fontSize: 20,
							fontWeight: 500,
							fontStyle: "italic",
							color: "var(--color-dashboard-text)",
						}}
					>
						{t("realtor.listings.share.title")}
					</DialogTitle>
					<DialogDescription className="text-sm text-dashboard-muted">
						{t("realtor.listings.share.description")}
					</DialogDescription>
				</DialogHeader>
				<div className="space-y-2">
					{hasNativeShare && (
						<Button
							onClick={handleNativeShare}
							className="w-full justify-start gap-2 bg-dashboard-accent hover:bg-dashboard-accent/90 text-dashboard-on-fill"
						>
							<Share2 className="h-4 w-4" />
							{t("realtor.listings.share.native")}
						</Button>
					)}
					<Button
						onClick={handleWhatsApp}
						variant="outline"
						className="w-full justify-start gap-2 border-dashboard-border text-dashboard-text bg-dashboard-bg hover:bg-dashboard-surface"
					>
						<MessageCircle className="h-4 w-4 text-dashboard-pill-done" />
						{t("realtor.listings.share.whatsapp")}
					</Button>
					<Button
						onClick={handleCopy}
						variant="outline"
						className="w-full justify-start gap-2 border-dashboard-border text-dashboard-text bg-dashboard-bg hover:bg-dashboard-surface"
					>
						{copied ? (
							<Check className="h-4 w-4 text-dashboard-pill-done" />
						) : (
							<Copy className="h-4 w-4" />
						)}
						{copied
							? t("realtor.listings.share.copied")
							: t("realtor.listings.share.copy")}
					</Button>
				</div>
				<div
					className="rounded-md border border-dashboard-border bg-dashboard-surface px-3 py-2 text-xs text-dashboard-muted break-all"
					style={{ fontFamily: "var(--font-mono)" }}
				>
					{content.url}
				</div>
			</DialogContent>
		</Dialog>
	);
}
